import RoutesComponent from "./components/Routes.tsx";
import BannerList from "./components/BannerList.tsx";
import { MigrationReportBanner } from "./components/notifications/MigrationReportBanner.tsx";
import axios from "axios";
import {AuthContext} from "./context/AuthContext.tsx";
import {User} from "./hooks/useUser.ts";
import {BrowserRouter, useLocation} from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { WebSocketProvider } from "./context/WebSocketContext.tsx";
import { useWebSocketCacheSync } from "./hooks/useWebSocketCacheSync.ts";
import { useOrganizationSubscription } from "./hooks/useWebSocketSubscription.ts";
import { useGatewayClaimBeacon } from "./hooks/useGatewayClaimBeacon.ts";
import { GatewayClaimContext } from "./hooks/useGatewayClaim.ts";
import { useGatewayDiscoveryToast } from "./hooks/useGatewayDiscoveryToast.ts";
import { usePermission } from "./hooks/usePermission.ts";
import { ActiveContextProvider } from "./context/ActiveContextContext.tsx";
import { useActiveContext } from "./hooks/useActiveContext.ts";
import { useActivateContext } from "./hooks/useActivateContext.ts";
import { Toaster } from "./components/ui/sonner.tsx";
import { useSearchParams } from "react-router";
import { StrictMode, useEffect, useRef } from "react";
import { useAuth } from "./hooks/useAuth.ts";
import { StripeProvider } from "@/features/billing";
import { InstallBanner, syncDisplayModeHeader } from "@/features/pwa";
import { EnableNotificationsBanner } from "@/features/webPush";
import { config } from "./config.ts";
import { SessionExpiredDialog } from "./components/auth/SessionExpiredDialog.tsx";
import { WrongContextPrompt } from "./components/auth/WrongContextPrompt.tsx";
import { SuspendedApp } from "./components/billing/SuspendedApp.tsx";
import { useAccountSuspension } from "./hooks/useAccountSuspension.ts";
import { markSessionExpired } from "./lib/sessionExpiry.ts";
import { accountCameFromLink, getActiveAccountId, setActiveAccountId } from "./lib/activeAccount.ts";
import { AppShell } from "./components/shell/AppShell.tsx";
import { TopBar } from "./components/shell/TopBar.tsx";
import { SideNav } from "./components/shell/SideNav.tsx";
import { BottomNav } from "./components/shell/BottomNav.tsx";

// Dev serves the API from another origin, so the session cookie only rides along when
// asked for. Set once at load rather than in a render: child effects fire their queries
// before App's own effects run, and a render-time write mutates shared state mid-render.
if (import.meta.env.DEV) {
  axios.defaults.withCredentials = true;
}

/**
 * Sign this tab's account out (the HttpOnly cookie can only be cleared by the backend) and go
 * to the sign-in page.
 */
async function clearAuthAndRedirect() {
  try {
    await axios.post(`${config.backendApi}/api/auth/logout`, "", { withCredentials: true });
  } catch {
    // Backend may be unreachable — redirect anyway.
  }
  window.location.href = "/signin";
}

/**
 * Initializes the active context, with a `?ctx=<contextId>` deep-link
 * override (the "hybrid" model).
 *
 * - When a URL carries `?ctx=`, it wins over both sessionStorage and the
 *   user's persisted default: we activate that context (POST /activate,
 *   which also persists it server-side), then strip the param via a
 *   history *replace* so it doesn't linger in history or get re-shared
 *   with the wrong recipient. This is what makes shared/email links land
 *   in the right org/division regardless of the session's prior context.
 * - With no `?ctx=`, we fall back to the user's persisted
 *   `activeContext` exactly as before. In-app navigation never carries
 *   the param, so normal browsing URLs stay clean.
 */
function ActiveContextInitializer() {
  const { user } = useAuth();
  const { activeContextId, setActiveContextId } = useActiveContext();
  const { mutate: activateContext } = useActivateContext();
  const [searchParams, setSearchParams] = useSearchParams();
  // Guards against re-firing activation for the same param under
  // StrictMode double-invoke / re-renders while the request is in flight.
  const handledCtxRef = useRef<string | null>(null);

  const ctxParam = searchParams.get('ctx');

  // Deep-link path: `?ctx=` overrides the persisted default.
  useEffect(() => {
    if (!user || !ctxParam) return;

    // Strip `?ctx=` regardless of outcome; keep history clean (replace).
    const stripParam = () => {
      setSearchParams(
        (prev) => {
          prev.delete('ctx');
          return prev;
        },
        { replace: true },
      );
    };

    // Already on this context — just clean the URL, no switch needed.
    if (ctxParam === activeContextId) {
      stripParam();
      return;
    }

    if (handledCtxRef.current === ctxParam) return;
    handledCtxRef.current = ctxParam;

    // On failure (no access) the shared hook toasts and leaves the
    // current context intact; we still strip the param either way.
    activateContext(ctxParam, { onSettled: stripParam });
  }, [user, ctxParam, activeContextId, activateContext, setSearchParams]);

  // Fallback path: no deep-link → initialize from the persisted context.
  useEffect(() => {
    if (ctxParam) return; // the deep-link path owns initialization
    if (user && !activeContextId) {
      const contextId = user.activeContext?.id;
      if (contextId) {
        setActiveContextId(contextId);
      }
    }
  }, [user, activeContextId, setActiveContextId, ctxParam]);

  return null;
}

/**
 * Component to enable WebSocket cache synchronization and organization-wide subscription
 * This must be inside WebSocketProvider
 *
 * Simple approach: Subscribe to all organization events on app load
 * - Ensures all pages get real-time updates automatically
 * - React Query only refetches active queries
 * - Minimal complexity, maximum developer velocity
 */
function CacheSyncManager() {
  useOrganizationSubscription();
  useWebSocketCacheSync();
  return null;
}

/**
 * Gateway Claim Beacon Manager
 *
 * Owns the single mDNS claim beacon and hands it to the tree via context.
 *
 * It is inert unless Local Network Access is already granted: mounting this no
 * longer emits anything. Beaconing starts either because the user previously
 * allowed the permission (a 60s background watch, so a gateway plugged in while
 * they're elsewhere in the app still gets noticed) or because they explicitly
 * clicked Scan on the Gateways page. Beaconing on load is what used to raise an
 * unexplained Chrome permission prompt on whatever page happened to be open.
 */
function GatewayClaimBeaconProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  // No point watching the network on behalf of someone the claim endpoint will
  // refuse: `gateway:create` is what `claim_gateway` enforces.
  const canAddGateways = usePermission('gateway:create');
  const beacon = useGatewayClaimBeacon(!!user && canAddGateways);

  return (
    <GatewayClaimContext.Provider value={beacon}>
      {children}
    </GatewayClaimContext.Provider>
  );
}

/**
 * Announces gateways the background watch turns up while the user is on some
 * other page. Must sit inside both the claim provider and the router.
 */
function GatewayDiscoveryAnnouncer() {
  useGatewayDiscoveryToast();
  return null;
}

/**
 * AppLayout — selects shell vs no-shell based on auth state.
 *
 * Authenticated users get the full AppShell (topbar + drawer + bottom
 * nav + banners). Unauthenticated users (signin, signup, password
 * reset flows, email verification landing) get the bare routes — those
 * pages style themselves as centered cards and don't need the shell
 * chrome. This matches typical SaaS UX and fixes the prior behavior
 * where SideNav rendered broken links on signin.
 */
function AppLayout() {
  const { user } = useAuth();
  const { suspended } = useAccountSuspension();
  const { pathname } = useLocation();

  // Sign-in is bare even for a signed-in user, who reaches it to add another account.
  if (!user || pathname === '/signin') {
    return <RoutesComponent />;
  }

  // A suspended account gets the lockout surface in place of the app, not a
  // banner above it — everything still readable behind a banner is a reason to
  // put off paying. SuspendedApp brings its own chrome and routes.
  if (suspended) {
    return <SuspendedApp />;
  }

  return (
    <AppShell
      topbar={<TopBar />}
      drawer={<SideNav />}
      bottomNav={<BottomNav />}
      banners={
        <>
          {/* Mutually exclusive by construction: the install nudge hides once the app is
              installed, which is the only state in which the notifications nudge shows. */}
          <InstallBanner />
          <EnableNotificationsBanner />
          <BannerList />
          <MigrationReportBanner />
        </>
      }
    >
      <RoutesComponent />
    </AppShell>
  );
}



function App() {
  const queryClient = useQueryClient();

  const fetchCurrentUser = async (): Promise<User | null> => {
    try {
      const response = await axios.get<User>(`${config.backendApi}/api/auth/current`, { withCredentials: true });
      // A 200 that isn't the user payload is not a session. When the backend has
      // no ready endpoint, Traefik answers /api/* from the frontend router — 200
      // text/html, the SPA's own index. Handing that back as a User gets past the
      // `!user` guards (a non-empty string is truthy) and throws in whichever
      // reader touches a field first, over a blank page. The throw falls through
      // the catch below unchanged and lands on the "Failed to load user" card.
      const payload: unknown = response.data;
      if (typeof payload !== 'object' || payload === null || typeof (payload as User).id !== 'string') {
        throw new Error('Unexpected response from /api/auth/current — is the backend reachable?');
      }
      // Pin the tab to the account it is showing (docs/MultiAccountSessions.md §3). Unpinned,
      // its requests would follow whichever account is the browser's first, which changes
      // under it when that account signs out in another tab.
      if (!getActiveAccountId() || accountCameFromLink()) {
        setActiveAccountId((payload as User).id);
      }
      return payload as User;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        e.response?.status === 401 &&
        (e.response.data as { error?: string } | undefined)?.error === 'account_signed_out' &&
        !queryClient.getQueryData<User | null>(['currentUser']) &&
        !accountCameFromLink()
      ) {
        // Loading a tab whose account has since been signed out of this browser: it moves to
        // the browser's first signed-in account, or to sign-in if none is left (§5). A link's
        // account is different, it stays named so sign-in can say whose link this is.
        setActiveAccountId(null);
        return fetchCurrentUser();
      }
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        // Two very different situations answer 401 here, and conflating them is
        // what used to make an expired session look like a signed-out one.
        if (queryClient.getQueryData<User | null>(['currentUser'])) {
          // We had a session and it lapsed. Throw rather than return null:
          // React Query keeps the last good user in cache on error, so the app
          // stays mounted and SessionExpiredDialog goes up over it instead of
          // the route guards bouncing to /signin and discarding the page.
          markSessionExpired();
          throw e;
        }
        return null; // Nobody was signed in — the ordinary signed-out answer.
      }
      // Network error or 5xx. Not an auth outcome; surface it as a failure
      // rather than quietly presenting it as "logged out".
      throw e;
    }
  };

  const { data: user, isLoading, isError, error } = useQuery<User | null>({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    // Re-validated on tab focus so an idle tab notices a lapsed session on its
    // own, rather than only when the user's next click happens to hit the API.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  // Only a query error with *no* cached user is a genuine failure to load. A
  // lapsed session also errors, but over a still-cached user, and that case
  // belongs to SessionExpiredDialog rather than to the error card below.
  const authFailed = isError && !user;

  // Keep the browser-tab title in sync with the configured product brand.
  useEffect(() => {
    document.title = config.appTitle;
  }, []);

  // Tell the backend when we are running as an installed app, so it gives this session the
  // longer window (docs/AuthSessions.md §3). On `axios.defaults` because the app calls the
  // bare `axios` export everywhere; an instance-scoped header would miss most requests.
  useEffect(() => syncDisplayModeHeader(axios.defaults), []);

  if (isLoading) {
    return <p>Loading, please wait...</p>;
  }

  if (authFailed) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="rounded-md border border-sev-1/40 bg-sev-1/10 p-4">
          <p className="mb-3 text-sm">
            Failed to load user: {error.message || 'An unknown error occurred.'}
          </p>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
            onClick={() => { void clearAuthAndRedirect(); }}
          >
            Clear session &amp; sign in
          </button>
        </div>
      </div>
    );
  }

  // StrictMode sits *inside* BrowserRouter on purpose. React 18 StrictMode
  // double-invokes state updaters in dev to surface impurities; React Router's
  // history navigation runs through such an updater, so with the router inside
  // StrictMode every navigation pushed *two* history entries (back/forward
  // needed two clicks). Keeping BrowserRouter outside StrictMode means its
  // navigation state isn't double-invoked, while the entire app tree below
  // still gets StrictMode's dev checks.
  return (
    <BrowserRouter>
      <StrictMode>
        {/* `authFailed` returns above, so reaching here means the user query
            succeeded or is failing over a still-cached user — a lapsed session,
            which the dialog owns. Consumers must not see an error state either
            way: ones that bail on `isError` (useIsMsp) would unregister routes
            and blank the page underneath the dialog. */}
        <AuthContext.Provider value={{ user, isLoading, isError: false, error: null }}>
          <ActiveContextProvider>
            <ActiveContextInitializer />
            <StripeProvider>
              <WebSocketProvider>
                <GatewayClaimBeaconProvider>
                  <CacheSyncManager />
                  <GatewayDiscoveryAnnouncer />
                  <AppLayout />
                  <SessionExpiredDialog />
                  <WrongContextPrompt />
                  <Toaster richColors position="top-right" />
                </GatewayClaimBeaconProvider>
              </WebSocketProvider>
            </StripeProvider>
          </ActiveContextProvider>
        </AuthContext.Provider>
      </StrictMode>
    </BrowserRouter>
  )
}

export default App
